import React from "react";
import Table from "@ui/Table";
import Banner from "@ui/Banner";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import formatDate from "@ui/i18n/formatDate";
import formatNumber from "@ui/i18n/formatNumber";
import Ellipsis from "@ui/Ellipsis";
import { SectionMessage } from "@ui/SectionMessage";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import t from "../../i18n";
import type { Source } from "../../api/useSources";
import Input from "@ui/Input/InputV2";
import Button from "@ui/Button";
import call from "../../api/call";
import { colors, fonts } from "@ui/Theme";
import Select, { type SelectOption } from "@ui/Select";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import useToasts from "@ui/Toast/useToasts";
import Spinner from "@ui/Spinner";

type Segment = {
  id: string;
  group: string | null;
  startDate: number;
  endDate: number;
  snapshotCount: number;
};

type PendingEdit = {
  id: string;
  type: "assignRange" | "deleteGroups";
  from?: number;
  to?: number;
  group?: string;
  groups?: string[];
  affectedCount: number;
};

type TimelineItem = {
  id: string;
  type: "segment" | "gap";
  startDate: number;
  endDate: number;
  group: string | null;
  snapshotCount?: number;
};

const columns = ["dateRange", "snapshotCount", "group", "actions"] as const;
type Column = (typeof columns)[number];

const computeRowKey = (row: Segment) => row.id;
const MAX_GROUP_LABEL_LENGTH = 20;
const noOp = () => {};

const truncateGroupLabel = (value: string) =>
  value.length > MAX_GROUP_LABEL_LENGTH
    ? `${value.slice(0, MAX_GROUP_LABEL_LENGTH - 1)}...`
    : value;

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const overlaps = (segment: Segment, from: number, to: number) =>
  segment.endDate >= from && segment.startDate <= to;

const durationInclusive = (start: number, end: number) => Math.max(end - start + 1, 1);

const allocateCounts = (total: number, weights: number[]) => {
  if (total <= 0) return weights.map(() => 0);
  const sum = weights.reduce((acc, w) => acc + w, 0);
  if (sum <= 0) return weights.map(() => 0);

  const raw = weights.map(w => (total * w) / sum);
  const base = raw.map(Math.floor);
  let remainder = total - base.reduce((acc, n) => acc + n, 0);
  if (remainder <= 0) return base;

  const order = raw
    .map((v, index) => ({ index, frac: v - base[index]! }))
    .sort((a, b) => b.frac - a.frac);
  for (const item of order) {
    if (remainder <= 0) break;
    base[item.index] = (base[item.index] ?? 0) + 1;
    remainder -= 1;
  }
  return base;
};

const splitSegmentForRange = (segment: Segment, from: number, to: number, nextGroup: string) => {
  if (!overlaps(segment, from, to)) return [segment];

  const overlapStart = Math.max(segment.startDate, from);
  const overlapEnd = Math.min(segment.endDate, to);
  if (overlapStart <= segment.startDate && overlapEnd >= segment.endDate) {
    return [{ ...segment, group: nextGroup }];
  }

  const parts: { startDate: number; endDate: number; group: string | null }[] = [];
  if (segment.startDate < overlapStart) {
    parts.push({
      startDate: segment.startDate,
      endDate: overlapStart - 1,
      group: segment.group,
    });
  }
  parts.push({
    startDate: overlapStart,
    endDate: overlapEnd,
    group: nextGroup,
  });
  if (segment.endDate > overlapEnd) {
    parts.push({
      startDate: overlapEnd + 1,
      endDate: segment.endDate,
      group: segment.group,
    });
  }

  const counts = allocateCounts(
    segment.snapshotCount,
    parts.map(part => durationInclusive(part.startDate, part.endDate)),
  );

  return parts
    .map((part, index) => ({
      id: `${part.group ?? "none"}-${part.startDate}-${part.endDate}-${index}`,
      group: part.group,
      startDate: part.startDate,
      endDate: part.endDate,
      snapshotCount: counts[index] ?? 0,
    }))
    .filter(part => part.snapshotCount > 0);
};

const mergeSegments = (segments: Segment[]) => {
  if (segments.length <= 1) return segments;
  const merged: Segment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (last && last.group === segment.group) {
      last.endDate = Math.max(last.endDate, segment.endDate);
      last.snapshotCount += segment.snapshotCount;
      continue;
    }
    merged.push({ ...segment });
  }
  return merged.map((segment, index) => ({
    ...segment,
    id: `${segment.group ?? "none"}-${segment.startDate}-${index}`,
  }));
};

const applyEditToSegments = (segments: Segment[], edit: PendingEdit) => {
  const updated = segments.flatMap(segment => {
    if (edit.type === "deleteGroups") {
      const groups = new Set(edit.groups ?? []);
      return groups.has(segment.group ?? "") ? [{ ...segment, group: null }] : [segment];
    }
    if (edit.from === undefined || edit.to === undefined || edit.group === undefined)
      return [segment];
    return splitSegmentForRange(segment, edit.from, edit.to, edit.group);
  });
  return mergeSegments(updated);
};

const applyEditsToSegments = (segments: Segment[], edits: PendingEdit[]) =>
  edits.reduce((acc, edit) => applyEditToSegments(acc, edit), segments);

const buildTimelineItems = (segments: Segment[]) => {
  const items: TimelineItem[] = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment) continue;
    items.push({
      id: `segment-${segment.id}`,
      type: "segment",
      startDate: segment.startDate,
      endDate: segment.endDate,
      group: segment.group,
      snapshotCount: segment.snapshotCount,
    });

    const next = segments[i + 1];
    if (!next) continue;

    const gapDuration = next.startDate - segment.endDate;
    if (gapDuration > ONE_DAY_MS) {
      items.push({
        id: `gap-${segment.endDate}-${next.startDate}`,
        type: "gap",
        startDate: segment.endDate,
        endDate: next.startDate,
        group: null,
        snapshotCount: 0,
      });
    }
  }

  return items;
};

const groupPalette = [
  colors.raw.chart_categorical_blue,
  colors.raw.chart_categorical_green,
  colors.raw.chart_categorical_orange,
  colors.raw.chart_categorical_red,
  colors.raw.chart_grenache,
  colors.raw.chart_sarcelle,
  colors.raw.chart_phosphorus_600,
  colors.raw.chart_prune,
  colors.raw.chart_turquoise,
  colors.raw.chart_chlorine_800,
  colors.raw.chart_dark_nitrogen_700,
  colors.raw.chart_chocolat,
  colors.raw.chart_moutarde,
];

const getTextColor = (hex: string) => {
  const normalized = hex.replace("#", "");
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? colors.raw.carbon900 : colors.raw.hydrogen;
};

const countSnapshotsForEdit = (segments: Segment[], edit: PendingEdit) => {
  if (edit.type === "deleteGroups") {
    const groups = new Set(edit.groups ?? []);
    return segments.reduce(
      (acc, segment) => (groups.has(segment.group ?? "") ? acc + segment.snapshotCount : acc),
      0,
    );
  }

  if (edit.from === undefined || edit.to === undefined || edit.group === undefined) return 0;
  return segments.reduce((acc, segment) => {
    const overlapStart = Math.max(segment.startDate, edit.from!);
    const overlapEnd = Math.min(segment.endDate, edit.to!);
    const hasOverlap = overlapStart <= overlapEnd;
    if (!hasOverlap || segment.group === edit.group) return acc;
    const overlapDuration = durationInclusive(overlapStart, overlapEnd);
    const segmentDuration = durationInclusive(segment.startDate, segment.endDate);
    const estimated = Math.round((segment.snapshotCount * overlapDuration) / segmentDuration);
    return acc + Math.min(segment.snapshotCount, Math.max(0, estimated));
  }, 0);
};

const formatDateTimeLocal = (
  date: Date,
  hour = date.getHours(),
  minute = date.getMinutes(),
  second = date.getSeconds(),
) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(hour).padStart(2, "0");
  const minutes = String(minute).padStart(2, "0");
  const seconds = String(second).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const floorToSecond = (date: number) => Math.floor(date / 1000) * 1000;
const ceilToSecond = (date: number) => Math.ceil(date / 1000) * 1000;

function GroupsModalContent({ source, isOpen }: { source: Source; isOpen: boolean }) {
  const [baseSegments, setBaseSegments] = React.useState<Segment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [isApplying, setIsApplying] = React.useState(false);
  const [rangeGroupName, setRangeGroupName] = React.useState("");
  const [rangeFrom, setRangeFrom] = React.useState("");
  const [rangeTo, setRangeTo] = React.useState("");
  const [deleteGroupNames, setDeleteGroupNames] = React.useState<string[]>([]);
  const [pendingEdits, setPendingEdits] = React.useState<PendingEdit[]>([]);
  const [showRangeCopiedMessage, setShowRangeCopiedMessage] = React.useState(false);
  const { addToast } = useToasts();
  const rangeCopiedTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setBaseSegments([]);
      setError(null);
      setLoading(false);
      setSelectedSegmentId(null);
      setPendingEdits([]);
      setShowRangeCopiedMessage(false);
      return;
    }

    const loadGroups = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await call("GET", "/sources/:sourceId/groups/segments", {
          routeParams: { sourceId: source.id },
        });
        const normalizedSegments = res.body.segments
          .map((segment, index) => ({
            id: `${segment.group ?? "none"}-${segment.startsAt}-${index}`,
            group: segment.group,
            startDate: segment.startsAt ? new Date(segment.startsAt).getTime() : null,
            endDate: segment.endsAt ? new Date(segment.endsAt).getTime() : null,
            snapshotCount: segment.snapshotCount,
          }))
          .filter((ns): ns is Segment => ns.startDate !== null && ns.endDate !== null);
        setBaseSegments(normalizedSegments);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadGroups();
  }, [isOpen, source.id, refreshKey]);
  const previewSegments = React.useMemo(
    () => applyEditsToSegments(baseSegments, pendingEdits),
    [baseSegments, pendingEdits],
  );
  const timelineItems = React.useMemo(() => buildTimelineItems(previewSegments), [previewSegments]);
  const selectedSegment = React.useMemo(
    () => previewSegments.find(segment => segment.id === selectedSegmentId) ?? null,
    [previewSegments, selectedSegmentId],
  );
  const existingGroupNames = React.useMemo(() => {
    const uniqueNames = new Set<string>();
    for (const segment of previewSegments) {
      if (segment.group) uniqueNames.add(segment.group);
    }
    return Array.from(uniqueNames).sort((a, b) => a.localeCompare(b));
  }, [previewSegments]);
  const groupColorMap = React.useMemo(
    () =>
      new Map(
        existingGroupNames.map((groupName, index) => [
          groupName,
          groupPalette[index % groupPalette.length] ?? colors.raw.chart_categorical_blue,
        ]),
      ),
    [existingGroupNames],
  );
  const getGroupColor = React.useCallback(
    (group: string | null) => {
      if (!group) {
        return { background: colors.raw.titanium200, text: colors.raw.carbon800 };
      }
      const background = groupColorMap.get(group) ?? colors.raw.chart_categorical_blue;
      return { background, text: getTextColor(background) };
    },
    [groupColorMap],
  );
  const deleteGroupOptions = React.useMemo<SelectOption[]>(
    () => existingGroupNames.map(name => ({ label: name, value: name })),
    [existingGroupNames],
  );
  const sourceMinDateInput = React.useMemo(
    () =>
      source.minSnapshotDate
        ? formatDateTimeLocal(new Date(source.minSnapshotDate), 0, 0, 0)
        : undefined,
    [source.minSnapshotDate],
  );
  const sourceMaxDateInput = React.useMemo(
    () =>
      source.maxSnapshotDate
        ? formatDateTimeLocal(new Date(source.maxSnapshotDate), 23, 59, 59)
        : undefined,
    [source.maxSnapshotDate],
  );
  const sourceMaxDateDefaultInput = React.useMemo(
    () =>
      source.maxSnapshotDate
        ? formatDateTimeLocal(new Date(source.maxSnapshotDate), 0, 0, 0)
        : undefined,
    [source.maxSnapshotDate],
  );

  React.useEffect(
    () => () => {
      if (rangeCopiedTimeoutRef.current !== null) {
        window.clearTimeout(rangeCopiedTimeoutRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!isOpen) {
      if (rangeCopiedTimeoutRef.current !== null) {
        window.clearTimeout(rangeCopiedTimeoutRef.current);
        rangeCopiedTimeoutRef.current = null;
      }
      setRangeFrom("");
      setRangeTo("");
      setShowRangeCopiedMessage(false);
      return;
    }
    if (!sourceMinDateInput || !sourceMaxDateDefaultInput) {
      setRangeFrom("");
      setRangeTo("");
      return;
    }
    setRangeFrom(sourceMinDateInput);
    setRangeTo(sourceMaxDateDefaultInput);
  }, [isOpen, sourceMaxDateDefaultInput, sourceMinDateInput]);

  React.useEffect(() => {
    if (!selectedSegment) {
      if (sourceMinDateInput && sourceMaxDateDefaultInput) {
        setRangeFrom(sourceMinDateInput);
        setRangeTo(sourceMaxDateDefaultInput);
      }
      setRangeGroupName("");
      return;
    }

    setRangeFrom(formatDateTimeLocal(new Date(floorToSecond(selectedSegment.startDate))));
    setRangeTo(formatDateTimeLocal(new Date(ceilToSecond(selectedSegment.endDate))));
    setRangeGroupName(selectedSegment.group ?? "");
  }, [selectedSegment, sourceMaxDateDefaultInput, sourceMinDateInput]);

  const createEditId = React.useCallback(
    () => `edit-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    [],
  );

  const handleAssignRange = React.useCallback(() => {
    if (!rangeGroupName.trim()) {
      addToast("error", t("sources.groups.editor.error.group_required"));
      return;
    }
    if (!rangeFrom || !rangeTo) {
      addToast("error", t("sources.groups.editor.error.invalid_range"));
      return;
    }
    const fromDate = new Date(rangeFrom).getTime();
    const toDate = new Date(rangeTo).getTime();
    if (Number.isNaN(fromDate) || Number.isNaN(toDate) || fromDate > toDate) {
      addToast("error", t("sources.groups.editor.error.invalid_range"));
      return;
    }

    const edit: PendingEdit = {
      id: createEditId(),
      type: "assignRange",
      from: fromDate,
      to: toDate,
      group: rangeGroupName.trim(),
      affectedCount: countSnapshotsForEdit(previewSegments, {
        id: "",
        type: "assignRange",
        from: fromDate,
        to: toDate,
        group: rangeGroupName.trim(),
        affectedCount: 0,
      }),
    };
    setPendingEdits(edits => [...edits, edit]);
    setRangeGroupName("");
  }, [addToast, createEditId, previewSegments, rangeFrom, rangeGroupName, rangeTo]);

  const handleDeleteGroup = React.useCallback(() => {
    if (deleteGroupNames.length === 0) {
      addToast("error", t("sources.groups.editor.error.delete_required"));
      return;
    }

    const edit: PendingEdit = {
      id: createEditId(),
      type: "deleteGroups",
      groups: deleteGroupNames,
      affectedCount: countSnapshotsForEdit(previewSegments, {
        id: "",
        type: "deleteGroups",
        groups: deleteGroupNames,
        affectedCount: 0,
      }),
    };
    if (edit.affectedCount === 0) {
      addToast("error", t("sources.groups.editor.error.group_not_found"));
      return;
    }
    setPendingEdits(edits => [...edits, edit]);
    setDeleteGroupNames([]);
  }, [addToast, createEditId, deleteGroupNames, previewSegments]);

  const handleConfirmChanges = React.useCallback(async () => {
    if (pendingEdits.length === 0) return;
    setIsApplying(true);

    try {
      await call("POST", "/sources/:sourceId/groups/segments", {
        routeParams: { sourceId: source.id },
        body: {
          operations: pendingEdits.map(edit => {
            if (edit.type === "deleteGroups") {
              return {
                type: "deleteGroups" as const,
                groups: edit.groups ?? [],
              };
            }
            return {
              type: edit.type,
              from: new Date(edit.from ?? 0),
              to: new Date(edit.to ?? 0),
              group: edit.group ?? "",
            };
          }),
        },
      });

      setPendingEdits([]);
      addToast("success", t("sources.groups.editor.success"));
      setRefreshKey(k => k + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      addToast("error", message);
    } finally {
      setIsApplying(false);
    }
  }, [addToast, pendingEdits, source.id]);

  const handleClearPending = React.useCallback(() => {
    setPendingEdits([]);
  }, []);

  const showSelectionFeedback = React.useCallback(() => {
    setShowRangeCopiedMessage(true);
    if (rangeCopiedTimeoutRef.current !== null) {
      window.clearTimeout(rangeCopiedTimeoutRef.current);
    }
    rangeCopiedTimeoutRef.current = window.setTimeout(() => {
      setShowRangeCopiedMessage(false);
      rangeCopiedTimeoutRef.current = null;
    }, 1000);
  }, []);

  const pendingDescriptions = React.useMemo(
    () =>
      pendingEdits.map(edit => {
        if (edit.type === "deleteGroups") {
          return {
            id: edit.id,
            description: t("sources.groups.editor.pending.item.delete", {
              groups: (edit.groups ?? []).join(", "),
              count: edit.affectedCount,
            }),
          };
        }
        const from = edit.from ? formatDate(new Date(edit.from), "datetime", null) : "";
        const to = edit.to ? formatDate(new Date(edit.to), "datetime", null) : "";
        if (edit.type === "assignRange") {
          return {
            id: edit.id,
            description: t("sources.groups.editor.pending.item.range", {
              group: edit.group ?? "",
              from,
              to,
              count: edit.affectedCount,
            }),
          };
        }
        return {
          id: edit.id,
          description: t("sources.groups.editor.pending.item.segment", {
            group: edit.group ?? "",
            from,
            to,
            count: edit.affectedCount,
          }),
        };
      }),
    [pendingEdits],
  );
  const isSelectedCycleUnchanged =
    selectedSegment !== null && rangeGroupName.trim() === (selectedSegment.group ?? "").trim();

  const metadata = React.useMemo<{ [key in Column]: ColumnMetaData<Segment> }>(
    () => ({
      dateRange: {
        label: t("sources.groups.table.column.dateRange"),
        render: row => (
          <DateRangeCell>
            <DateRangeValue>{formatDate(new Date(row.startDate), "datetime", null)}</DateRangeValue>
            <DateRangeArrow>{String.fromCharCode(8594)}</DateRangeArrow>
            <DateRangeValue>{formatDate(new Date(row.endDate), "datetime", null)}</DateRangeValue>
          </DateRangeCell>
        ),
        width: "350px",
        sortable: false,
      },
      snapshotCount: {
        label: t("sources.table.column.snapshotCount"),
        render: row => formatNumber(row.snapshotCount),
        width: "120px",
        sortable: false,
      },
      group: {
        label: t("sources.groups.table.column.group"),
        render: row => {
          const { background, text } = getGroupColor(row.group);
          const groupLabel = row.group ?? t("sources.groups.table.group.none");
          return (
            <GroupBadge
              style={{ backgroundColor: background, color: text }}
              title={groupLabel}
              aria-label={groupLabel}
            >
              <Ellipsis>{truncateGroupLabel(groupLabel)}</Ellipsis>
            </GroupBadge>
          );
        },
        width: "140px",
        sortable: false,
      },
      actions: {
        label: "",
        render: row => (
          <Button
            variant="link"
            slim
            onClick={() => {
              setSelectedSegmentId(row.id);
              showSelectionFeedback();
            }}
            title={t("sources.groups.table.action.select")}
          >
            {t("sources.groups.table.action.select")}
          </Button>
        ),
        width: "150px",
        sortable: false,
      },
    }),
    [getGroupColor, showSelectionFeedback],
  );

  if (error) {
    return <SectionMessage state="error" content={t("sources.groups.error", { details: error })} />;
  }

  if (!loading && previewSegments.length === 0) {
    return <EmptyState>{t("sources.groups.empty")}</EmptyState>;
  }

  return (
    <ModalLayout>
      <ScrollArea>
        <Content>
          <TablePane>
            <TableSection>
              {loading && baseSegments.length === 0 ? (
                <TableLoadingState>
                  <Spinner width={24} height={24} />
                </TableLoadingState>
              ) : (
                <>
                  {timelineItems.length > 0 ? (
                    <Timeline>
                      {timelineItems.map(item => {
                        const { background } =
                          item.type === "gap"
                            ? { background: colors.raw.hydrogen }
                            : getGroupColor(item.group);
                        const duration = Math.max(item.endDate - item.startDate, 1);
                        const label =
                          item.type === "gap"
                            ? t("sources.groups.timeline.gap")
                            : (item.group ?? t("sources.groups.table.group.none"));
                        const startLabel = formatDate(new Date(item.startDate), "datetime", null);
                        const endLabel = formatDate(new Date(item.endDate), "datetime", null);
                        const tooltipText =
                          item.type === "gap" ? (
                            <>
                              <TooltipLine>
                                {t("sources.groups.editor.range.from")}: {startLabel}
                              </TooltipLine>
                              <TooltipLine>
                                {t("sources.groups.editor.range.to")}: {endLabel}
                              </TooltipLine>
                            </>
                          ) : (
                            <>
                              <TooltipLine>
                                {t("sources.groups.editor.range.from")}: {startLabel}
                              </TooltipLine>
                              <TooltipLine>
                                {t("sources.groups.editor.range.to")}: {endLabel}
                              </TooltipLine>
                              <TooltipLine>
                                {t("sources.table.column.snapshotCount")}:{" "}
                                {formatNumber(item.snapshotCount ?? 0)}
                              </TooltipLine>
                            </>
                          );
                        return (
                          <TooltipV2
                            key={item.id}
                            placement="top"
                            overlay={<OverlayWrapper title={label} text={tooltipText} />}
                          >
                            <TimelineSegment
                              style={{ flexGrow: duration, backgroundColor: background }}
                              aria-label={label}
                            />
                          </TooltipV2>
                        );
                      })}
                    </Timeline>
                  ) : null}
                  <Table
                    loading={loading}
                    datas={previewSegments}
                    metadata={metadata}
                    computeRowKey={computeRowKey}
                  />
                </>
              )}
            </TableSection>
          </TablePane>
          <EditorPane>
            <Editor>
              <EditorSection>
                <SectionTitle>{t("sources.groups.editor.range.title")}</SectionTitle>
                <FormRow>
                  <SelectionFeedbackCallout
                    className={showRangeCopiedMessage ? selectionFeedbackSuccess : undefined}
                    uiType={showRangeCopiedMessage ? "valid" : "incomplete"}
                    onClose={noOp}
                  >
                    {showRangeCopiedMessage
                      ? t("sources.groups.editor.range.copied")
                      : t("sources.groups.editor.range.none")}
                  </SelectionFeedbackCallout>
                </FormRow>
                <FormRow>
                  <Input
                    type="datetime-local"
                    label={t("sources.groups.editor.range.from")}
                    value={rangeFrom}
                    onChange={e => setRangeFrom(e.currentTarget.value)}
                    min={sourceMinDateInput}
                    max={sourceMaxDateInput}
                    step="1"
                  />
                  <Input
                    type="datetime-local"
                    label={t("sources.groups.editor.range.to")}
                    value={rangeTo}
                    onChange={e => setRangeTo(e.currentTarget.value)}
                    min={sourceMinDateInput}
                    max={sourceMaxDateInput}
                    step="1"
                  />
                </FormRow>
                <FormRow>
                  <Input
                    label={t("sources.groups.editor.group.label")}
                    value={rangeGroupName}
                    onChange={e => setRangeGroupName(e.currentTarget.value)}
                  />
                </FormRow>
                <FormRow>
                  <ActionButton
                    variant="secondary"
                    onClick={handleAssignRange}
                    disabled={
                      isApplying ||
                      !rangeGroupName.trim() ||
                      !rangeFrom ||
                      !rangeTo ||
                      isSelectedCycleUnchanged
                    }
                  >
                    {t("sources.groups.editor.range.apply")}
                  </ActionButton>
                </FormRow>
              </EditorSection>

              <EditorSection>
                <SectionTitle>{t("sources.groups.editor.delete.title")}</SectionTitle>
                <FormRow>
                  <Select
                    multiple
                    label={t("sources.groups.editor.delete.from")}
                    prompt={t("sources.groups.editor.delete.prompt")}
                    options={deleteGroupOptions}
                    value={deleteGroupNames}
                    onChange={e =>
                      setDeleteGroupNames(
                        Array.from(e.currentTarget.selectedOptions).map(o => o.value),
                      )
                    }
                  />
                </FormRow>
                <FormRow>
                  <ActionButton
                    variant="destructive"
                    onClick={handleDeleteGroup}
                    disabled={isApplying || deleteGroupNames.length === 0}
                  >
                    {t("sources.groups.editor.delete.apply")}
                  </ActionButton>
                </FormRow>
              </EditorSection>
            </Editor>
          </EditorPane>
        </Content>
      </ScrollArea>
      <StickyActions>
        <FooterPending>
          <SectionHeader>
            <SectionTitle>{t("sources.groups.editor.pending.title")}</SectionTitle>
            <PendingCount>{formatNumber(pendingEdits.length)}</PendingCount>
          </SectionHeader>
          {pendingEdits.length === 0 ? (
            <PendingEmpty>{t("sources.groups.editor.pending.empty")}</PendingEmpty>
          ) : (
            <PendingList>
              {pendingDescriptions.map(item => (
                <PendingItem key={item.id}>{item.description}</PendingItem>
              ))}
            </PendingList>
          )}
        </FooterPending>
        <FooterActions>
          <ActionButton
            variant="secondary"
            onClick={handleClearPending}
            disabled={pendingEdits.length === 0 || isApplying}
          >
            {t("sources.groups.editor.pending.clear")}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={handleConfirmChanges}
            disabled={pendingEdits.length === 0 || isApplying}
          >
            {t("sources.groups.editor.pending.confirm")}
          </ActionButton>
        </FooterActions>
      </StickyActions>
    </ModalLayout>
  );
}

export default React.memo(GroupsModalContent);

const EmptyState = styled.div`
  padding: 16px;
`;

const TableLoadingState = styled.div`
  min-height: 64px;
  display: grid;
  place-items: center;
`;

const Content = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  min-height: 0;

  @media (min-width: 1380px) {
    grid-template-columns: minmax(0, 1.6fr) minmax(340px, 0.8fr);
    align-items: stretch;
    height: 100%;
  }

  .Field > label {
    font: ${fonts.body2SemiBold};
  }
`;

const ModalLayout = styled.div`
  max-height: 70vh;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  min-height: 0;

  @media (min-width: 1380px) {
    overflow-y: hidden;
    padding-right: 0;
  }
`;

const TablePane = styled.div`
  min-width: 0;
  min-height: 0;

  @media (min-width: 1380px) {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    padding-right: 6px;
  }
`;

const EditorPane = styled.div`
  min-width: 0;
  min-height: 0;

  @media (min-width: 1380px) {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    padding-right: 6px;
  }
`;

const Editor = styled.div`
  display: grid;
  gap: 12px;
  align-content: start;
`;

const EditorSection = styled.section`
  display: grid;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${colors.titanium200};
  border-radius: 8px;
  background: ${colors.hydrogen};
`;

const SectionTitle = styled.div`
  font: ${fonts.body1SemiBold};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  align-items: center;
`;

const SelectionFeedbackCallout = styled(Banner)`
  grid-column: 1 / -1;
  position: relative;
  top: auto;
  z-index: auto;
  padding: 10px 12px 10px 14px;
  border-radius: 10px;
  background: ${colors.titanium50};
  min-height: 44px;
  transition:
    background-color 180ms ease-out,
    color 180ms ease-out;

  &&&,
  &&& * {
    color: ${colors.carbon700};
  }

  .wrapper {
    margin-inline: 0;
    justify-content: flex-start;
  }

  button {
    display: none;
  }
`;

const GroupBadge = styled.div`
  border-radius: 999px;
  padding: 2px 8px;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
`;

const TableSection = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${colors.titanium200};
  border-radius: 8px;
  background: ${colors.hydrogen};
  overflow-x: auto;
`;

const Timeline = styled.div`
  display: flex;
  width: 100%;
  height: 18px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${colors.titanium200};
`;

const TimelineSegment = styled.div`
  min-width: 6px;
  height: 100%;
`;

const TooltipLine = styled.span`
  display: block;
  white-space: nowrap;
`;

const DateRangeCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const DateRangeValue = styled.span`
  white-space: nowrap;
`;

const DateRangeArrow = styled.span`
  color: ${colors.carbon500};
  flex: 0 0 auto;
`;

const ActionButton = styled(Button)`
  width: max-content;
  justify-self: start;
`;

const PendingList = styled.ul`
  margin: 0;
  padding-left: 20px;
  display: grid;
  gap: 6px;
`;

const PendingItem = styled.li`
  color: ${colors.carbon800};
  font: ${fonts.body2};
`;

const PendingEmpty = styled.div`
  color: ${colors.carbon600};
  font: ${fonts.body2};
`;

const PendingCount = styled.span`
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${colors.titanium200};
  color: ${colors.carbon900};
  font: ${fonts.body2SemiBold};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
`;

const StickyActions = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  margin: 8px -32px 0;
  padding: 12px 32px 0;
  background: ${colors.hydrogen};
  border-top: 1px solid ${colors.titanium200};
`;

const FooterPending = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
`;

const selectionFeedbackSuccess = css`
  @keyframes source-editor-selection-feedback {
    0% {
      opacity: 0;
      transform: translateY(-1px);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &&& {
    background: ${colors.chlorine100};
  }

  &&&,
  &&& .wrapper div,
  &&& .wrapper div * {
    color: ${colors.carbon900};
  }

  &&& svg {
    color: ${colors.chlorine700};
  }

  animation: source-editor-selection-feedback 220ms ease-out;
`;
